import React from 'react';
import { PenLine, Sparkles, ChevronRight, RotateCw, MessageCircle } from 'lucide-react';

const AMBER = '#D4A574';

// The hero re-titles itself as the calendar's arc widens: an ordinary day, the
// close of a week (Saturday), a month, or a whole year. Each carries its own
// gentle fallback so the card is never blank while the recommendation loads.
// Exported so App's "Discuss with AI" sends the Companion exactly the question
// the card is showing — fallback included. Two copies of these strings would
// drift, and the drift would only show as the chat answering a question the
// user never read.
export const CADENCE = {
  daily:   { eyebrow: 'Reflection of the Day',  fallback: 'What is one small moment that brought you peace today?' },
  weekly:  { eyebrow: 'Reflection of the Week',  fallback: 'Looking back on your week, what are you taking with you?' },
  monthly: { eyebrow: 'A Look Back This Month',  fallback: 'As the month closes, what mattered most to you in it?' },
  yearly:  { eyebrow: 'Your Year in Review',     fallback: 'If this year were a chapter in your story, what would you title it?' },
};

/**
 * Home hero card: the recommended reflection prompt ("Reflections of the Day").
 * Presentational — recommendation data + handlers are supplied by App. Only ever
 * shows simple_prompt (the short, human question), never the clinical framing.
 *
 * Feedback is asymmetric and gentle: engaging (Start Writing) is the implicit
 * positive — there is no thumbs-up. The single control on the right is a soft
 * "show me another" (rotate), which skips this prompt and its near-duplicate group.
 *
 * Two ways to take the prompt up sit side by side: write it, or talk it through
 * with the Companion (`onDiscuss`). Same question either way, so the second is
 * deliberately secondary in weight: an icon with a tooltip, never a competing
 * labelled primary.
 */
export default function ReflectionOfTheDay({ loading, prompt, onStartWriting, onSkip, busy, onPersonalize, onDiscuss }) {
  const { eyebrow, fallback } = CADENCE[prompt?.cadence] || CADENCE.daily;
  const text = prompt?.simplePrompt;
  const showSkeleton = loading || busy;
  const canSkip = !loading && !!prompt?.promptId;

  return (
    <div className="relative bg-white border border-stone-100 rounded-[2rem] px-7 py-7 shadow-sm overflow-hidden">
      <Sparkles
        size={130}
        className="absolute -top-8 -right-8 text-stone-100 pointer-events-none"
        strokeWidth={1.25}
        aria-hidden="true"
      />
      <div className="relative z-10">
        {/* Top row: cadence eyebrow (left) + category (top-right corner). */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <PenLine size={16} strokeWidth={2.5} style={{ color: AMBER }} />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: AMBER }}>
              {eyebrow}
            </span>
          </div>
          {!loading && prompt?.category && (
            <span className="inline-flex items-center gap-1.5 flex-shrink-0 pt-0.5 text-[11px] font-medium tracking-wide text-stone-400">
              <span className="h-1 w-1 rounded-full" style={{ backgroundColor: AMBER }} aria-hidden="true" />
              {prompt.category}
            </span>
          )}
        </div>

        {showSkeleton ? (
          <div className="space-y-3 animate-pulse" aria-hidden="true">
            <div className="h-6 w-11/12 rounded-full bg-stone-100" />
            <div className="h-6 w-3/4 rounded-full bg-stone-100" />
          </div>
        ) : (
          <p className="font-serif text-[26px] leading-snug text-stone-800">
            &ldquo;{text || fallback}&rdquo;
          </p>
        )}

        {/* Bottom row: the two ways in (Start Writing + the icon-only Discuss,
            which together fit a 360pt phone on one line) + "show another"
            (right, same level). */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={onStartWriting}
              disabled={showSkeleton}
              aria-label="Start writing today's reflection"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-3 text-[15px] font-semibold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200"
              style={{ backgroundColor: '#FBF3E7', color: '#B4741F' }}
            >
              Start Writing <ChevronRight size={18} />
            </button>

            {onDiscuss && (
              // Icon-only, so Start Writing stays the one labelled primary and
              // the whole row fits a 360pt phone. The slow aurora ring (violet →
              // amber, the Companion's colours) marks it as the AI without a
              // word; the tooltip names it on hover and keyboard focus.
              <span className="group/discuss relative inline-flex">
                <style>{`
                  @keyframes rotd-aurora { to { transform: rotate(360deg); } }
                  .rotd-aurora { animation: rotd-aurora 12s linear infinite; }
                  @media (prefers-reduced-motion: reduce) { .rotd-aurora { animation: none; } }
                `}</style>
                <button
                  type="button"
                  onClick={onDiscuss}
                  disabled={showSkeleton}
                  aria-label="Discuss today's reflection with the AI companion"
                  className="relative h-12 w-12 rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_6px_22px_-6px_rgba(139,92,246,0.55)] active:scale-95 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-200 focus-visible:ring-offset-2"
                >
                  <span aria-hidden="true" className="absolute inset-0 overflow-hidden rounded-full">
                    <span className="rotd-aurora absolute -inset-1/2 bg-[conic-gradient(from_0deg,#8B5CF6,#C4B5FD,#E8B86D,#F5D0A9,#A78BFA,#8B5CF6)]" />
                  </span>
                  <span aria-hidden="true" className="absolute inset-[1.5px] flex items-center justify-center rounded-full bg-[radial-gradient(circle_at_50%_40%,#FFFFFF_0%,#FBF8FF_60%,#F3EEFE_100%)] transition-colors duration-300 group-hover/discuss:bg-[radial-gradient(circle_at_50%_40%,#FFFFFF_0%,#F5F0FF_55%,#EAE1FD_100%)]">
                    <MessageCircle size={20} strokeWidth={2} className="text-[#7C4DDB]" />
                    <Sparkles size={11} strokeWidth={2.4} className="absolute right-[9px] top-[8px] fill-[#E8B86D] text-[#D99A4E]" />
                  </span>
                </button>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-full bg-[#2D2A27] px-3 py-1.5 text-[12px] font-medium text-[#F0EDE8] opacity-0 shadow-md transition-all duration-200 ease-out group-hover/discuss:translate-y-0 group-hover/discuss:opacity-100 group-focus-within/discuss:translate-y-0 group-focus-within/discuss:opacity-100"
                >
                  Discuss with AI
                </span>
              </span>
            )}
          </div>

          {canSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={busy}
              aria-label="Show me a different reflection"
              title="Show me another"
              className="group h-9 w-9 rounded-full flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 active:scale-90 transition-all duration-200 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
            >
              <RotateCw
                size={17}
                strokeWidth={2.2}
                className="transition-transform duration-500 ease-out group-hover:rotate-180 motion-reduce:transition-none motion-reduce:group-hover:rotate-0"
              />
            </button>
          )}
        </div>

        {/* Quiet provenance line, not a warning: today's prompt is a real one
            from the catalog, it just wasn't matched to anything the user wrote.
            Lowest-emphasis element on the card — the prompt is the point. */}
        {!showSkeleton && prompt && prompt.personalized === false && onPersonalize && (
          <button
            type="button"
            onClick={onPersonalize}
            className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-stone-400 hover:text-stone-600 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300 rounded-full px-1"
          >
            <Sparkles size={13} strokeWidth={2} aria-hidden="true" />
            Personalize with AI
          </button>
        )}
      </div>
    </div>
  );
}
